import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { LOAChaserProgress, LOAChaserWidget } from '../../components/LOAChaser';
import axiosInstance from '../../services/axiosConfig';

// Types
interface SLAStep {
  step_id: string;
  label: string;
  delay_days: number;
  step_type: string;
  requires_manual_task: boolean;
  escalation_level: number;
}

interface Matter {
  id: string;
  name: string;
  status: string;
}

interface Coverage {
  id: string;
  matter_id: string;
  category: string | null;
  carrier: {
    name: string | null;
  } | null;
  status: string | null;
}

interface Campaign {
  id: string;
  matter_id: string;
  coverage_id: string;
  workflow_id: string;
  email_thread_id?: string;
  status: string;
  current_step_index: number;
  current_step_label: string;
  next_action_date: string | null;
  next_action_type: string | null;
  next_action_label: string;
  last_contact_date: string | null;
  total_contact_count: number;
  created_at: string;
}

interface CampaignDetail {
  campaign: Campaign;
  workflow_state: {
    status: string;
    current_step_index: number;
    current_step_label: string;
    next_action_date: string | null;
    next_action_type: string | null;
    last_contact_date: string | null;
    total_contacts: number;
  } | null;
}

// Signal types
const SIGNAL_TYPES = [
  {
    value: 'skip_timer',
    label: 'Skip Timer (Execute Now)',
    description: 'Execute current step immediately',
  },
  {
    value: 'loa_detected',
    label: 'LOA Detected (Pause)',
    description: 'Pauses the workflow',
  },
  {
    value: 'loa_task_completed',
    label: 'LOA Task Completed',
    description: 'Completes the workflow',
  },
  {
    value: 'task_completed',
    label: 'Task Completed',
    description: 'Manual task done',
  },
  { value: 'stop', label: 'Stop Campaign', description: 'Stop with reason' },
  {
    value: 'resume',
    label: 'Resume Campaign',
    description: 'Resume stopped/paused',
  },
  { value: 'snooze', label: 'Snooze', description: 'Delay next action' },
  { value: 'set_step', label: 'Set Step', description: 'Jump to step' },
  {
    value: 'manual_activity',
    label: 'Manual Activity',
    description: 'Log manual contact',
  },
];

// Status colors
const getStatusColor = (
  status: string,
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  switch (status) {
    case 'running':
      return 'success';
    case 'paused':
      return 'warning';
    case 'completed_loa_received':
      return 'info';
    case 'stopped_manually':
      return 'error';
    case 'monthly_cadence':
      return 'secondary';
    default:
      return 'default';
  }
};

export const LOAOutreachTesting = () => {
  // Tab state - 0 = Sandbox (existing), 1 = Widget Test (new)
  const [activeTab, setActiveTab] = useState(0);

  // State
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] =
    useState<CampaignDetail | null>(null);
  const [slaSteps, setSlaSteps] = useState<SLAStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [signalLoading, setSignalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Launch form state
  const [matterSearch, setMatterSearch] = useState('');
  const [matters, setMatters] = useState<Matter[]>([]);
  const [selectedMatter, setSelectedMatter] = useState<Matter | null>(null);
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [selectedCoverageId, setSelectedCoverageId] = useState('');
  const [emailThreadId, setEmailThreadId] = useState('');
  const [startingStepIndex, setStartingStepIndex] = useState(0);
  const [mattersLoading, setMattersLoading] = useState(false);
  const [coveragesLoading, setCoveragesLoading] = useState(false);

  // Widget test state
  const [widgetMatterSearch, setWidgetMatterSearch] = useState('');
  const [widgetMatters, setWidgetMatters] = useState<Matter[]>([]);
  const [widgetSelectedMatter, setWidgetSelectedMatter] =
    useState<Matter | null>(null);
  const [widgetCoverages, setWidgetCoverages] = useState<Coverage[]>([]);
  const [widgetSelectedCoverageId, setWidgetSelectedCoverageId] = useState('');
  const [widgetMattersLoading, setWidgetMattersLoading] = useState(false);
  const [widgetCoveragesLoading, setWidgetCoveragesLoading] = useState(false);
  const [widgetKey, setWidgetKey] = useState(0); // Key to force widget remount
  const [forceLauncher, setForceLauncher] = useState(false); // Force show launcher even if campaign exists

  // Signal form state
  const [signalType, setSignalType] = useState('loa_detected');
  const [signalParams, setSignalParams] = useState<
    Record<string, string | number>
  >({});

  // Fetch data on mount
  useEffect(() => {
    fetchSlaSteps();
    fetchCampaigns();
  }, []);

  const fetchSlaSteps = async () => {
    try {
      const response = await axiosInstance.get(
        '/api/zaddy/loa-outreach/sla-steps/',
      );
      setSlaSteps(response.data.steps);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to fetch SLA steps';
      console.error('Failed to fetch SLA steps:', errorMessage);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        '/api/zaddy/loa-outreach/campaigns/',
      );
      setCampaigns(response.data.campaigns);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to fetch campaigns';
      setError(errorMessage || 'Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignDetail = async (campaignId: string) => {
    console.log('Fetching campaign detail for:', campaignId);
    try {
      const response = await axiosInstance.get(
        `/api/zaddy/loa-outreach/campaigns/${campaignId}/`,
      );
      console.log('Campaign detail response:', response.data);
      setSelectedCampaign(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch campaign detail:', err);
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to fetch campaign details';
      toast.error(errorMessage || 'Failed to fetch campaign details');
    }
  };

  const searchMatters = async (search: string) => {
    if (!search || search.length < 2) {
      setMatters([]);
      return;
    }
    setMattersLoading(true);
    try {
      const response = await axiosInstance.get(`/api/matters/`, {
        params: { search },
      });
      setMatters(response.data || []);
    } catch (err: unknown) {
      console.error('Failed to search matters:', err);
      setMatters([]);
    } finally {
      setMattersLoading(false);
    }
  };

  const fetchCoverages = async (matterId: string) => {
    setCoveragesLoading(true);
    setCoverages([]);
    setSelectedCoverageId('');
    try {
      const response = await axiosInstance.get(
        `/api/matters/${matterId}/coverages/`,
      );
      setCoverages(response.data || []);
    } catch (err: unknown) {
      console.error('Failed to fetch coverages:', err);
      setCoverages([]);
    } finally {
      setCoveragesLoading(false);
    }
  };

  const handleMatterSelect = (matter: Matter) => {
    setSelectedMatter(matter);
    setMatterSearch(matter.name);
    setMatters([]);
    fetchCoverages(matter.id);
  };

  // Widget test functions
  const searchWidgetMatters = async (search: string) => {
    if (!search || search.length < 2) {
      setWidgetMatters([]);
      return;
    }
    setWidgetMattersLoading(true);
    try {
      const response = await axiosInstance.get(`/api/matters/`, {
        params: { search },
      });
      setWidgetMatters(response.data || []);
    } catch {
      setWidgetMatters([]);
    } finally {
      setWidgetMattersLoading(false);
    }
  };

  const fetchWidgetCoverages = async (matterId: string) => {
    setWidgetCoveragesLoading(true);
    setWidgetCoverages([]);
    setWidgetSelectedCoverageId('');
    try {
      const response = await axiosInstance.get(
        `/api/matters/${matterId}/coverages/`,
      );
      setWidgetCoverages(response.data || []);
    } catch {
      setWidgetCoverages([]);
    } finally {
      setWidgetCoveragesLoading(false);
    }
  };

  const handleWidgetMatterSelect = (matter: Matter) => {
    setWidgetSelectedMatter(matter);
    setWidgetMatterSearch(matter.name);
    setWidgetMatters([]);
    fetchWidgetCoverages(matter.id);
  };

  const handleWidgetReset = () => {
    setWidgetSelectedCoverageId('');
    setWidgetKey((k) => k + 1); // Force widget remount
  };

  // Get selected coverage details for widget
  const widgetSelectedCoverage = widgetCoverages.find(
    (c) => c.id === widgetSelectedCoverageId,
  );

  const launchTestCampaign = async () => {
    if (!selectedMatter) {
      toast.error('Please select a matter');
      return;
    }
    if (!selectedCoverageId) {
      toast.error('Please select a coverage');
      return;
    }

    setLaunchLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        '/api/zaddy/loa-outreach/launch-test/',
        {
          matter_id: selectedMatter.id,
          coverage_id: selectedCoverageId,
          email_thread_id: emailThreadId || 'test-thread-123',
          starting_step_index: startingStepIndex,
        },
      );
      toast.success(`Campaign launched: ${response.data.workflow_id}`);
      fetchCampaigns();
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to launch campaign';
      setError(errorMessage || 'Failed to launch campaign');
      toast.error(errorMessage || 'Failed to launch campaign');
    } finally {
      setLaunchLoading(false);
    }
  };

  const sendSignal = async (
    overrideSignal?: string,
    overrideParams?: Record<string, string | number>,
  ) => {
    if (!selectedCampaign) return;

    const signalToSend = overrideSignal || signalType;
    const paramsToSend = overrideParams || signalParams;

    setSignalLoading(true);
    try {
      const response = await axiosInstance.post(
        `/api/zaddy/loa-outreach/campaigns/${selectedCampaign.campaign.id}/signal/`,
        {
          signal: signalToSend,
          params: paramsToSend,
        },
      );
      toast.success(response.data.message);

      // If the response includes updated campaign state, update local state immediately
      if (response.data.campaign) {
        setSelectedCampaign((prev) =>
          prev
            ? {
                ...prev,
                campaign: {
                  ...prev.campaign,
                  ...response.data.campaign,
                },
              }
            : null,
        );
      }

      // For execution signals, poll until state changes or timeout
      // skip_timer and set_step with execute=1 run asynchronously
      const isExecutionSignal =
        signalToSend === 'skip_timer' ||
        (signalToSend === 'set_step' && Number(paramsToSend.execute) === 1);

      if (isExecutionSignal) {
        const previousStepIndex = selectedCampaign.campaign.current_step_index;
        const campaignId = selectedCampaign.campaign.id;

        // Poll for step change using recursive setTimeout to avoid eslint no-await-in-loop
        const pollForChange = (attemptsLeft: number): Promise<void> => {
          return new Promise((resolve) => {
            setTimeout(async () => {
              if (attemptsLeft <= 0) {
                resolve();
                return;
              }
              try {
                const pollResponse = await axiosInstance.get(
                  `/api/zaddy/loa-outreach/campaigns/${campaignId}/`,
                );
                const newStepIndex =
                  pollResponse.data.campaign.current_step_index;
                if (newStepIndex !== previousStepIndex) {
                  setSelectedCampaign(pollResponse.data);
                  fetchCampaigns();
                  resolve();
                  return;
                }
              } catch {
                // Continue polling on error
              }
              pollForChange(attemptsLeft - 1).then(resolve);
            }, 1000);
          });
        };

        await pollForChange(6);
      }

      // Fallback: refresh full campaign details and list
      await fetchCampaignDetail(selectedCampaign.campaign.id);
      fetchCampaigns();
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to send signal';
      toast.error(errorMessage || 'Failed to send signal');
    } finally {
      setSignalLoading(false);
    }
  };

  const deleteTestCampaigns = async () => {
    if (!window.confirm('Delete all test campaigns? This cannot be undone.'))
      return;

    try {
      const response = await axiosInstance.delete(
        '/api/zaddy/loa-outreach/test-campaigns/',
      );
      toast.success(response.data.message);
      setCampaigns([]);
      setSelectedCampaign(null);
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : 'Failed to delete campaigns';
      toast.error(errorMessage || 'Failed to delete campaigns');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Render signal params based on type
  const renderSignalParams = () => {
    switch (signalType) {
      case 'stop':
        return (
          <TextField
            label="Reason"
            value={signalParams.reason || ''}
            onChange={(e) =>
              setSignalParams({ ...signalParams, reason: e.target.value })
            }
            fullWidth
            size="small"
            placeholder="Reason for stopping"
          />
        );
      case 'snooze':
        return (
          <TextField
            label="Days"
            type="number"
            value={signalParams.days || 3}
            onChange={(e) =>
              setSignalParams({
                ...signalParams,
                days: parseInt(e.target.value, 10),
              })
            }
            fullWidth
            size="small"
            inputProps={{ min: 1, max: 30 }}
          />
        );
      case 'set_step':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Step</InputLabel>
            <Select
              value={signalParams.step_id || ''}
              label="Step"
              onChange={(e) =>
                setSignalParams({ ...signalParams, step_id: e.target.value })
              }
            >
              {slaSteps.map((step) => (
                <MenuItem key={step.step_id} value={step.step_id}>
                  {step.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'manual_activity':
        return (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Activity Type</InputLabel>
              <Select
                value={signalParams.activity_type || 'manual_call'}
                label="Activity Type"
                onChange={(e) =>
                  setSignalParams({
                    ...signalParams,
                    activity_type: e.target.value,
                  })
                }
              >
                <MenuItem value="manual_call">Manual Call</MenuItem>
                <MenuItem value="manual_email">Manual Email</MenuItem>
                <MenuItem value="manual_fax">Manual Fax</MenuItem>
                <MenuItem value="supervisor_escalation">
                  Supervisor Escalation
                </MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={signalParams.notes || ''}
              onChange={(e) =>
                setSignalParams({ ...signalParams, notes: e.target.value })
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Tabs Header */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Sandbox (Manual Signals)" />
          <Tab label="Widget Test (Production Flow)" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Panel - Campaign List */}
          <Box
            sx={{
              width: '350px',
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                LOA Outreach Sandbox
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Test LOA outreach workflow and signals
              </Typography>

              {/* Launch Controls */}
              <Stack spacing={2}>
                {/* Matter Search */}
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    label="Search Matter"
                    value={matterSearch}
                    onChange={(e) => {
                      setMatterSearch(e.target.value);
                      setSelectedMatter(null);
                      searchMatters(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    placeholder="Enter matter name or ID"
                    InputProps={{
                      endAdornment: mattersLoading ? (
                        <CircularProgress size={16} />
                      ) : null,
                    }}
                  />
                  {matters.length > 0 && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {matters.map((matter) => (
                        <MenuItem
                          key={matter.id}
                          onClick={() => handleMatterSelect(matter)}
                          sx={{ fontSize: 13 }}
                        >
                          {matter.name}
                        </MenuItem>
                      ))}
                    </Paper>
                  )}
                </Box>

                {/* Coverage Selector */}
                <FormControl fullWidth size="small" disabled={!selectedMatter}>
                  <InputLabel>Coverage</InputLabel>
                  <Select
                    value={selectedCoverageId}
                    label="Coverage"
                    onChange={(e) => setSelectedCoverageId(e.target.value)}
                  >
                    {coveragesLoading && (
                      <MenuItem disabled>Loading...</MenuItem>
                    )}
                    {coverages.map((coverage) => (
                      <MenuItem key={coverage.id} value={coverage.id}>
                        {coverage.carrier?.name || 'Unknown Carrier'} -{' '}
                        {coverage.category || 'Unknown'}
                        {coverage.status && ` (${coverage.status})`}
                      </MenuItem>
                    ))}
                    {!coveragesLoading &&
                      coverages.length === 0 &&
                      selectedMatter && (
                        <MenuItem disabled>No coverages found</MenuItem>
                      )}
                  </Select>
                </FormControl>

                {/* Email Thread ID */}
                <TextField
                  label="Email Thread ID (Front Conversation)"
                  value={emailThreadId}
                  onChange={(e) => setEmailThreadId(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="cnv_xxxxx (optional)"
                  helperText="Leave blank for test default"
                />

                {/* Starting Step */}
                <FormControl fullWidth size="small">
                  <InputLabel>Starting Step</InputLabel>
                  <Select
                    value={startingStepIndex}
                    label="Starting Step"
                    onChange={(e) =>
                      setStartingStepIndex(e.target.value as number)
                    }
                  >
                    {slaSteps.map((step, index) => (
                      <MenuItem key={step.step_id} value={index}>
                        {index}: {step.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={launchTestCampaign}
                  disabled={
                    launchLoading || !selectedMatter || !selectedCoverageId
                  }
                  fullWidth
                >
                  {launchLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Launch Test Campaign'
                  )}
                </Button>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchCampaigns}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={deleteTestCampaigns}
                    sx={{ flex: 1 }}
                  >
                    Delete All
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {/* Campaign List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {error && (
                <Alert severity="error" sx={{ m: 2 }}>
                  {error}
                </Alert>
              )}

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loading && campaigns.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No campaigns found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Launch a test campaign to get started
                  </Typography>
                </Box>
              )}

              {campaigns.map((campaign) => (
                <Card
                  key={campaign.id}
                  sx={{
                    m: 1,
                    cursor: 'pointer',
                    bgcolor:
                      selectedCampaign?.campaign.id === campaign.id
                        ? 'action.selected'
                        : 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => fetchCampaignDetail(campaign.id)}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle2" noWrap sx={{ flex: 1 }}>
                        Step {campaign.current_step_index}
                      </Typography>
                      <Chip
                        label={campaign.status.replace(/_/g, ' ')}
                        size="small"
                        color={getStatusColor(campaign.status)}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {campaign.current_step_label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Next: {formatDate(campaign.next_action_date)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Right Panel - Campaign Details & Controls */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {!selectedCampaign && (
              <Box sx={{ textAlign: 'center', mt: 10 }}>
                <Typography variant="h6" color="text.secondary">
                  Select a campaign to view details and send signals
                </Typography>
              </Box>
            )}

            {selectedCampaign && (
              <Stack spacing={3}>
                {/* LOA Chaser Progress Component */}
                <LOAChaserProgress
                  campaign={selectedCampaign.campaign}
                  slaSteps={slaSteps}
                  onSendSignal={sendSignal}
                  onOpenEmail={
                    selectedCampaign.campaign.email_thread_id
                      ? () => {
                          const threadId =
                            selectedCampaign.campaign.email_thread_id;
                          // Front conversation URLs use the format: https://app.frontapp.com/open/{conversation_id}
                          window.open(
                            `https://app.frontapp.com/open/${threadId}`,
                            '_blank',
                          );
                        }
                      : undefined
                  }
                  loading={signalLoading}
                />

                {/* Campaign Technical Details (collapsed by default) */}
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    Workflow ID: {selectedCampaign.campaign.workflow_id}
                  </Typography>
                </Paper>

                {/* Signal Controls */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Send Signal
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Signal Type</InputLabel>
                      <Select
                        value={signalType}
                        label="Signal Type"
                        onChange={(e) => {
                          const newSignalType = e.target.value;
                          setSignalType(newSignalType);
                          // Set default params for signal types that require them
                          if (newSignalType === 'manual_activity') {
                            setSignalParams({ activity_type: 'manual_call' });
                          } else if (newSignalType === 'snooze') {
                            setSignalParams({ days: 3 });
                          } else {
                            setSignalParams({});
                          }
                        }}
                      >
                        {SIGNAL_TYPES.map((sig) => (
                          <MenuItem key={sig.value} value={sig.value}>
                            {sig.label} - {sig.description}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {renderSignalParams()}

                    <Button
                      variant="contained"
                      onClick={() => sendSignal()}
                      disabled={signalLoading}
                      fullWidth
                    >
                      {signalLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        'Send Signal'
                      )}
                    </Button>
                  </Stack>

                  {/* Quick Actions */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Quick Actions
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        color="warning"
                        disabled={signalLoading}
                        onClick={() => sendSignal('loa_detected', {})}
                      >
                        Pause (LOA Detected)
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="success"
                        disabled={signalLoading}
                        onClick={() => sendSignal('loa_task_completed', {})}
                      >
                        Complete (Task Done)
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        disabled={signalLoading}
                        onClick={() => sendSignal('resume', {})}
                      >
                        Resume
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        disabled={signalLoading}
                        onClick={() =>
                          sendSignal('stop', { reason: 'Test stop' })
                        }
                      >
                        Stop
                      </Button>
                    </Stack>
                  </Box>
                </Paper>

                {/* SLA Steps Reference */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    SLA Steps Reference
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Click &quot;Execute&quot; to run current step immediately,
                    or &quot;Reset&quot;/&quot;Skip&quot; to jump to another
                    step
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1}>
                    {slaSteps.map((step, index) => {
                      const isCurrentStep =
                        index === selectedCampaign.campaign.current_step_index;
                      const isPastStep =
                        index < selectedCampaign.campaign.current_step_index;
                      const isFutureStep =
                        index > selectedCampaign.campaign.current_step_index;
                      const canExecute =
                        isCurrentStep &&
                        selectedCampaign.campaign.status === 'running';
                      const canJumpToStep =
                        !isCurrentStep &&
                        (selectedCampaign.campaign.status === 'running' ||
                          selectedCampaign.campaign.status === 'paused');

                      return (
                        <Box
                          key={step.step_id}
                          sx={{
                            p: 1.5,
                            bgcolor: isCurrentStep
                              ? 'primary.light'
                              : isPastStep
                                ? 'success.light'
                                : 'grey.50',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            opacity: isPastStep ? 0.7 : 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: isCurrentStep
                                ? 'primary.main'
                                : isPastStep
                                  ? 'success.main'
                                  : 'grey.400',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                            }}
                          >
                            {isPastStep ? '✓' : index}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                              {step.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              +{step.delay_days} days |{' '}
                              {step.step_type.replace(/_/g, ' ')}
                              {step.requires_manual_task && ' | Manual'}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {/* Execute button for current step */}
                            {isCurrentStep && (
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                disabled={!canExecute || signalLoading}
                                onClick={() => sendSignal('skip_timer', {})}
                                sx={{ minWidth: 80 }}
                              >
                                {signalLoading ? 'Running...' : 'Execute'}
                              </Button>
                            )}
                            {/* Reset button for past steps */}
                            {isPastStep && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                disabled={!canJumpToStep || signalLoading}
                                onClick={() =>
                                  sendSignal('set_step', {
                                    step_id: step.step_id,
                                  })
                                }
                                sx={{ minWidth: 80 }}
                              >
                                Reset
                              </Button>
                            )}
                            {/* Skip button for future steps */}
                            {isFutureStep && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="info"
                                disabled={!canJumpToStep || signalLoading}
                                onClick={() =>
                                  sendSignal('set_step', {
                                    step_id: step.step_id,
                                  })
                                }
                                sx={{ minWidth: 80 }}
                              >
                                Skip to
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Box>
        </Box>
      )}

      {/* Widget Test Tab */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left Panel - Matter/Coverage Selection */}
          <Box
            sx={{
              width: '350px',
              borderRight: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Widget Test Mode
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Test the LOAChaserWidget as it would appear in production
              </Typography>

              <Stack spacing={2}>
                {/* Matter Search */}
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    label="Search Matter"
                    value={widgetMatterSearch}
                    onChange={(e) => {
                      setWidgetMatterSearch(e.target.value);
                      setWidgetSelectedMatter(null);
                      searchWidgetMatters(e.target.value);
                    }}
                    fullWidth
                    size="small"
                    placeholder="Enter matter name or ID"
                    InputProps={{
                      endAdornment: widgetMattersLoading ? (
                        <CircularProgress size={16} />
                      ) : null,
                    }}
                  />
                  {widgetMatters.length > 0 && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 200,
                        overflow: 'auto',
                      }}
                    >
                      {widgetMatters.map((matter) => (
                        <MenuItem
                          key={matter.id}
                          onClick={() => handleWidgetMatterSelect(matter)}
                          sx={{ fontSize: 13 }}
                        >
                          {matter.name}
                        </MenuItem>
                      ))}
                    </Paper>
                  )}
                </Box>

                {/* Coverage Selector */}
                <FormControl
                  fullWidth
                  size="small"
                  disabled={!widgetSelectedMatter}
                >
                  <InputLabel>Coverage</InputLabel>
                  <Select
                    value={widgetSelectedCoverageId}
                    label="Coverage"
                    onChange={(e) => {
                      setWidgetSelectedCoverageId(e.target.value);
                      setWidgetKey((k) => k + 1); // Force widget remount
                    }}
                  >
                    {widgetCoveragesLoading && (
                      <MenuItem disabled>Loading...</MenuItem>
                    )}
                    {widgetCoverages.map((coverage) => (
                      <MenuItem key={coverage.id} value={coverage.id}>
                        {coverage.carrier?.name || 'Unknown Carrier'} -{' '}
                        {coverage.category || 'Unknown'}
                        {coverage.status && ` (${coverage.status})`}
                      </MenuItem>
                    ))}
                    {!widgetCoveragesLoading &&
                      widgetCoverages.length === 0 &&
                      widgetSelectedMatter && (
                        <MenuItem disabled>No coverages found</MenuItem>
                      )}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceLauncher}
                      onChange={(e) => {
                        setForceLauncher(e.target.checked);
                        setWidgetKey((k) => k + 1); // Force remount
                      }}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Force show launcher (ignore existing campaign)
                    </Typography>
                  }
                />

                {widgetSelectedCoverageId && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleWidgetReset}
                  >
                    Reset Widget
                  </Button>
                )}
              </Stack>
            </Box>

            {/* Instructions */}
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                How to test:
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
              >
                <ol style={{ paddingLeft: 16, margin: 0 }}>
                  <li style={{ marginBottom: 8 }}>
                    Search and select a matter
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Select a coverage from that matter
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    If no campaign exists, you&apos;ll see the launcher UI
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Search for an email thread and start the campaign
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Once started, the progress bar will appear
                  </li>
                  <li>Use the controls to interact with the campaign</li>
                </ol>
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                Selected:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Matter: {widgetSelectedMatter?.name || 'None'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Coverage: {widgetSelectedCoverage?.carrier?.name || 'None'}
                {widgetSelectedCoverage?.category &&
                  ` - ${widgetSelectedCoverage.category}`}
              </Typography>
            </Box>
          </Box>

          {/* Right Panel - Widget Preview */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'grey.50' }}>
            {!widgetSelectedMatter || !widgetSelectedCoverageId ? (
              <Box sx={{ textAlign: 'center', mt: 10 }}>
                <Typography variant="h6" color="text.secondary">
                  Select a matter and coverage to preview the widget
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxWidth: 800 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2 }}
                  color="text.secondary"
                >
                  Widget Preview (as it would appear on coverage page):
                </Typography>
                <LOAChaserWidget
                  key={widgetKey}
                  matterId={widgetSelectedMatter.id}
                  coverageId={widgetSelectedCoverageId}
                  slaSteps={slaSteps}
                  existingCampaign={forceLauncher ? null : undefined}
                  useSandbox
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
